/**
 * Consent Preference Manager — localStorage under dlpm_consent_v1.
 * Categories: necessary (always true), analytics, marketing.
 */

export const CONSENT_STORAGE_KEY = "dlpm_consent_v1";

const CONSENT_CHANGED = "dlpm:consent-changed";
const OPEN_PREFERENCES = "dlpm:open-consent";

/** @typedef {{ necessary: true, analytics: boolean, marketing: boolean, updatedAt: string }} ConsentState */

/**
 * @returns {ConsentState|null}
 */
export function getConsent() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      necessary: true,
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
      updatedAt: typeof parsed.updatedAt === "string" ? parsed.updatedAt : "",
    };
  } catch {
    return null;
  }
}

/**
 * @param {Partial<{ analytics: boolean, marketing: boolean }>} partial
 * @returns {ConsentState}
 */
export function setConsent(partial = {}) {
  const next = {
    necessary: true,
    analytics: Boolean(partial.analytics),
    marketing: Boolean(partial.marketing),
    updatedAt: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Quota / private mode — still emit so UI can proceed for this session.
  }
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGED, { detail: next }));
  return next;
}

/** @returns {boolean} */
export function hasAnswered() {
  return getConsent() !== null;
}

/**
 * @param {(state: ConsentState) => void} cb
 * @returns {() => void} unsubscribe
 */
export function onConsentChange(cb) {
  const handler = (event) => {
    cb(event.detail || getConsent());
  };
  window.addEventListener(CONSENT_CHANGED, handler);
  return () => window.removeEventListener(CONSENT_CHANGED, handler);
}

export function openConsentPreferences() {
  window.dispatchEvent(new CustomEvent(OPEN_PREFERENCES));
}

export function acceptAll() {
  return setConsent({ analytics: true, marketing: true });
}

export function rejectAll() {
  return setConsent({ analytics: false, marketing: false });
}

/** Event name for footer / banner reopen. */
export const OPEN_CONSENT_EVENT = OPEN_PREFERENCES;
